import { CorrectionStrategy, ValidationRuleTrigger } from '@erp/shared-types';
import type {
  CatalogAdvancedFilter,
  ValidationRuleDefinitionInput,
} from '@erp/rpc';
import { isValidCronExpression } from '../validationScheduleCron';

/**
 * Step decisions for the rule-builder wizard, as pure functions.
 *
 * Each one mirrors a rule the server enforces in `assertValidRuleDefinition`,
 * so an author is stopped on the step that owns the mistake instead of on a 400
 * from the save. The wizard's `validate` callbacks own the side effects
 * (previewing, recording field errors); the branching that decides *what*
 * should happen lives here so it can be tested directly.
 */

/** Longest rule name the contract accepts. */
export const MAX_RULE_NAME_LENGTH = 255;

/**
 * Field names are compared the way the server compares them — trimmed and
 * case-insensitive — so `ITEMNO` and `itemno` are one field, not two.
 */
export function fieldKey(field: string): string {
  return field.trim().toUpperCase();
}

// ─── 1. Table / schema ───────────────────────────────────────────────────────

export type SchemaStepDecision = 'ok' | 'need-schema' | 'unknown-schema';

export function schemaStepDecision(state: {
  schemaName: string;
  /** The schemas `listRuleSchemas` offered. */
  knownSchemaNames: readonly string[];
}): SchemaStepDecision {
  if (!state.schemaName.trim()) return 'need-schema';
  return state.knownSchemaNames.includes(state.schemaName)
    ? 'ok'
    : 'unknown-schema';
}

// ─── 3. Grouping fields ──────────────────────────────────────────────────────

export type GroupingStepDecision = 'ok' | 'need-grouping';

export function groupingStepDecision(state: {
  groupingFields: readonly string[];
}): GroupingStepDecision {
  return state.groupingFields.length > 0 ? 'ok' : 'need-grouping';
}

// ─── 4. Consistent fields ────────────────────────────────────────────────────

export type ConsistentFieldsProblem =
  | { kind: 'need-validated' }
  /**
   * A field that is both grouped by and checked would agree within its own
   * group by construction, so the check would be vacuous — the server rejects
   * it, and so does this step.
   */
  | { kind: 'overlaps-grouping'; fields: string[] };

/** The step-4 problem, or null when the two field lists are usable. */
export function consistentFieldsProblem(state: {
  groupingFields: readonly string[];
  validatedFields: readonly string[];
}): ConsistentFieldsProblem | null {
  if (state.validatedFields.length === 0) return { kind: 'need-validated' };

  const grouped = new Set(state.groupingFields.map(fieldKey));
  const fields = state.validatedFields.filter((field) =>
    grouped.has(fieldKey(field)),
  );
  return fields.length > 0 ? { kind: 'overlaps-grouping', fields } : null;
}

// ─── 6. Correction strategy ──────────────────────────────────────────────────

export type StrategyStepDecision =
  'ok' | 'need-preferred-division' | 'unknown-preferred-division';

export function strategyStepDecision(state: {
  correctionStrategy: CorrectionStrategy;
  /** `core_data_sources.id` of the division whose value wins, or ''. */
  preferredDataSourceId: string;
  /** Divisions the org has this schema in — the server's own denominator. */
  divisionIds: readonly string[];
}): StrategyStepDecision {
  if (state.correctionStrategy !== CorrectionStrategy.PreferredSource) {
    return 'ok';
  }
  if (!state.preferredDataSourceId) return 'need-preferred-division';
  return state.divisionIds.includes(state.preferredDataSourceId)
    ? 'ok'
    : 'unknown-preferred-division';
}

// ─── 7. Owner / trigger ──────────────────────────────────────────────────────

export type TriggerStepProblem = 'need-cron' | 'invalid-cron';

/**
 * The step-7 problem, or null when the trigger and its schedule agree.
 *
 * Mirrors `scheduleCronError` on the server so the author is stopped on the step
 * that owns the mistake rather than by a 400 from the save. The server remains
 * the authority — this is the courtesy copy.
 */
export function triggerStepProblem(state: {
  executionTrigger: ValidationRuleTrigger;
  scheduleCron: string;
}): TriggerStepProblem | null {
  if (state.executionTrigger !== ValidationRuleTrigger.Scheduled) return null;
  const expression = state.scheduleCron.trim();
  if (!expression) return 'need-cron';
  return isValidCronExpression(expression) ? null : 'invalid-cron';
}

// ─── 8. Preview ──────────────────────────────────────────────────────────────

/**
 * A stable identity for the definition a preview was run against, so stepping
 * back and forth doesn't re-evaluate the same rule, and editing anything the
 * evaluator reads does.
 */
export function ruleDefinitionKey(
  definition: ValidationRuleDefinitionInput,
): string {
  return JSON.stringify([
    definition.schemaName,
    definition.rowFilters ?? [],
    definition.groupingFields,
    definition.validatedFields,
    [
      definition.normalization.trimWhitespace,
      definition.normalization.caseInsensitive,
      definition.normalization.blankEqualsNull,
    ],
    definition.correctionStrategy,
    definition.strategyConfig?.preferredDataSourceId ?? null,
  ]);
}

export type PreviewStepDecision = 'ok' | 'run' | 'wait' | 'blocked';

/**
 * Whether the preview step can be left: `run` evaluates the current
 * definition, `wait` means one is already in flight (entering the step starts
 * one), and `blocked` means the definition it already evaluated was rejected
 * and nothing has changed since.
 */
export function previewStepDecision(state: {
  definitionKey: string;
  /** The definition the last preview attempt ran against, or null. */
  previewedKey: string | null;
  /** Whether that attempt failed. */
  previewFailed: boolean;
  /** Whether a preview is in flight right now. */
  previewing: boolean;
}): PreviewStepDecision {
  if (state.previewing) return 'wait';
  if (state.previewedKey !== state.definitionKey) return 'run';
  return state.previewFailed ? 'blocked' : 'ok';
}

// ─── 9. Save ─────────────────────────────────────────────────────────────────

export type SaveFieldProblem = 'missing-name' | 'name-too-long';

/** Field-level problems on the save step, keyed by the field they belong to. */
export function saveStepProblems(state: { name: string }): {
  name?: SaveFieldProblem;
} {
  const name = state.name.trim();
  if (!name) return { name: 'missing-name' };
  if (name.length > MAX_RULE_NAME_LENGTH) return { name: 'name-too-long' };
  return {};
}

// ─── Shared shaping ──────────────────────────────────────────────────────────

/**
 * How the cron goes over the wire: trimmed, and `null` for any trigger other
 * than `scheduled`.
 *
 * Clearing it on a trigger change is what keeps a rule honest after an author
 * types a schedule and then switches to "someone runs it" — the server treats a
 * leftover cron as inert rather than rejecting it, so if the wizard sent it the
 * detail page would show a schedule that never fires.
 */
export function scheduleCronForSave(
  executionTrigger: ValidationRuleTrigger,
  scheduleCron: string,
): string | null {
  if (executionTrigger !== ValidationRuleTrigger.Scheduled) return null;
  return scheduleCron.trim() || null;
}

/**
 * How row filters go over the wire: an empty set is `null` ("every row of the
 * schema"), which is what a rule with no filters reads back as.
 */
export function rowFiltersForSave(
  filters: readonly CatalogAdvancedFilter[],
): CatalogAdvancedFilter[] | null {
  return filters.length > 0 ? [...filters] : null;
}

/**
 * Toggle a field in a selection, keeping it in the schema's own field order so
 * the chips read like the table they came from rather than like a click history.
 */
export function toggleFieldInOrder(
  selected: readonly string[],
  field: string,
  schemaFieldNames: readonly string[],
): string[] {
  const key = fieldKey(field);
  if (selected.some((candidate) => fieldKey(candidate) === key)) {
    return selected.filter((candidate) => fieldKey(candidate) !== key);
  }
  const next = new Set([...selected.map(fieldKey), key]);
  const ordered = schemaFieldNames.filter((name) => next.has(fieldKey(name)));
  // A field the schema no longer lists (a rule authored before a schema change)
  // keeps its place rather than silently vanishing from the selection.
  const missing = selected.filter(
    (candidate) =>
      !schemaFieldNames.some((name) => fieldKey(name) === fieldKey(candidate)),
  );
  return [...ordered, ...missing];
}
